import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification.js";
import { formatTimestamp } from "../../utils/TextUtils.js";
import { allowedToView } from "../../utils/PermissionUtils.js";

function Products() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const perPage = 10; // Items per page

    const userRole = localStorage.getItem("userRole");
    const token = localStorage.getItem("access_token");
    const navigate = useNavigate();

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            
            // Construct the URL with pagination and search parameters
            const url = new URL(`${API_URL}/products`);
            url.searchParams.append("page", currentPage);
            if (searchTerm.trim()) {
                url.searchParams.append("search", searchTerm.trim());
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setProducts(data.data);
                setTotalPages(data.last_page || 1);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch products");
            }
        } catch (error) {
            setError("An error occurred while fetching products.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, token, navigate]);

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null);
        } else {
            setIsDropdownOpen(index);
        }
    };

    const handleDeleteClick = (product) => {
        setSelectedProduct(product);
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteProduct = async () => {
        if (selectedProduct) {
            try {
                const response = await fetch(`${API_URL}/products/${selectedProduct.id}`, {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    setNotification({ type: "success", message: 'Product deleted successfully' });
                    fetchProducts(); // Refresh the list
                } else if (response.status === 401) {
                    navigate("/logout");
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: 'Product not found.' });
                } else {
                    setNotification({ type: "danger", message: 'There was an error.' });
                    throw new Error('Failed to delete product');
                }
            } catch (error) {
                setNotification({ message: 'An error occurred while deleting the product.', type: 'danger' });
                console.error("Error:", error);
            } finally {
                setIsConfirmDialogOpen(false);
                setSelectedProduct(null);
            }
        }
    };

    const cancelDeleteProduct = () => {
        setIsConfirmDialogOpen(false);
        setSelectedProduct(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    useEffect(() => {
        fetchProducts();
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [fetchProducts, isDropdownOpen]);

    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin', 'user']));
    };

    const canDelete = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin']));
    };

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Products</p>
                        </div>
                    </div>
                    <div className="level-right">
                        {canEdit(userRole) && (
                            <p className="level-item">
                                <Link to="add" className="button primary-bg">
                                    <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New
                                </Link>
                            </p>
                        )}
                    </div>
                </nav>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <div className="field">
                    <div className="control">
                        <input
                            className="input"
                            type="text"
                            placeholder="Search by product name"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </div>

                {loading ? (
                    <p>Loading products...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Price</th>
                                    <th>Posted At</th>
                                    <th>Status</th>
                                    <th>Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.length > 0 ? (
                                    products.map((product, index) => (
                                        <tr key={product.id}>
                                            <td>{index + 1 + (currentPage - 1) * perPage}</td>
                                            <td>
                                                <Link to={`${product.id}/details`} className="default-color">{product.name}</Link>
                                            </td>
                                            <td>{product.price && product.price}</td>
                                            <td>
                                                {product.created_at && (
                                                    `${formatTimestamp(product.created_at)}`
                                                )}
                                            </td>
                                            <td>{product.status || "N/A"}</td>
                                            <td>
                                                <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                    <div className="dropdown-trigger">
                                                        <button
                                                            className="button is-white"
                                                            aria-haspopup="true"
                                                            aria-controls="dropdown-menu"
                                                            onClick={() => toggleDropdown(index)}
                                                        >
                                                            <span className="icon">
                                                                <i className="fas fa-ellipsis-v"></i>
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                        <div className="dropdown-content">
                                                            <Link to={`${product.id}/details`} className="dropdown-item">
                                                                <span className="icon">
                                                                    <i className="fas fa-eye"></i>
                                                                </span>
                                                                <span>View</span>
                                                            </Link>
                                                            {canEdit(userRole) && (
                                                                <Link to={`${product.id}/update`} className="dropdown-item">
                                                                    <span className="icon">
                                                                        <i className="fas fa-edit"></i>
                                                                    </span>
                                                                    <span>Edit</span>
                                                                </Link>
                                                            )}
                                                            {canDelete(userRole) && (
                                                                <Link className="dropdown-item" onClick={() => handleDeleteClick(product)}>
                                                                    <span className="icon">
                                                                        <i className="fas fa-trash-alt"></i>
                                                                    </span>
                                                                    <span>Remove</span>
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="has-text-centered">No products found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        <nav className="pagination is-centered">
                            <button
                                className="pagination-previous"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                            <button
                                className="pagination-next"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </nav>
                    </>
                )}
            </main>
        </div>
    );
}

export default Products;